import { action, computed } from 'mobx';
import Model from 'core/model/Model';
import $auth from 'app/stores/$auth';
import $setup from 'app/stores/$setup';
import $dictionary from 'app/stores/$dictionary';

export default class CloudExportTask extends Model {
  get defaults() {
    const { currentRegion } = $auth;

    const isPortalHost = currentRegion === 'US' || currentRegion === '';
    const isEu = currentRegion === 'EU';

    let api_root = isPortalHost ? 'https://api.kentik.com' : `https://api.${currentRegion.toLowerCase()}.kentik.com`;
    let flow_dest = isPortalHost ? 'https://flow.kentik.com' : `https://flow.${currentRegion.toLowerCase()}.kentik.com`;
    if (isEu) {
      api_root = 'https://api.kentik.eu';
      flow_dest = 'https://flow.kentik.eu';
    }

    return {
      enabled: true,
      properties: {
        apply_bgp: false,
        api_root,
        flow_dest,
        task_status: 'PENDING'
      }
    };
  }

  @computed
  get hasDevices() {
    const devices = this.get('devices');

    return devices && devices.length > 0;
  }

  @action
  resetTaskStatus() {
    const properties = this.get('properties');

    return this.save({
      enabled: true,
      properties: {
        ...properties,
        task_status: 'PENDING',
        error_message: ''
      }
    });
  }

  @action
  async save(attributes = {}, options = {}) {
    return super.save(attributes, options).then((result) => {
      $setup.getPlans({ force: true });

      return result;
    });
  }

  get urlRoot() {
    return '/api/ui/cloudExport';
  }

  get sortValues() {
    return {
      properties: () => this.get('properties.gce_project') || this.get('properties.aws_region')
    };
  }

  get omitDuringSerialize() {
    return ['devices', 'fps2hTotal', 'fps5mTotal', 'flowVol30dTotal', 'awsChecker', 'mouseover', 'plan'];
  }

  get isKentikManagedS3() {
    return !!this.get('properties.kentik_managed_s3_region');
  }

  get awsBucketPath() {
    if (this.get('properties.kentik_managed_s3_region')) {
      return `arn:aws:s3:::${this.get('properties.aws_bucket')}`;
    }
    return `${this.get('properties.aws_bucket')}.s3-${this.get('properties.aws_region')}.amazonaws.com`;
  }

  get removalConfirmText() {
    return {
      title: 'Remove Cloud Export',
      text: `Are you sure you want to remove ${this.get('name')}?`
    };
  }

  /*
    Populates the properties.log_collection_types array with an optional list of logs we want to collect

    when:

    [] - metadata-only export
    ['flow'] - collect flow logs
    ['vnet'] - collect azure vnet logs
    ['firewall'] - collect firewall logs (currently Azure-only support)
    ['gcp_cloud_run'] - collect gcp_cloud_run logs (GCP-only support)
  */
  getLogCollectionTypes({ cloudProvider, exportDetails }) {
    const logCollectionTypes = [];

    // 'manual_setup_type' is a relic of old v1 onboarding
    // v2 cloud config wizard uses enable_flow_log_collection
    const flow =
      exportDetails.manual_setup_type === 'collect_flow_logs' || exportDetails?.properties?.enable_flow_log_collection;
    const gcpCloudRun = exportDetails.manual_setup_type === 'gcp_cloud_run';

    if (cloudProvider === 'azure') {
      // azure has their own switch that can override the manual_setup_type
      // azure_collect_flow_logs should always be true for exports created using v2 cloud config wizard
      // set to collect NSG and/or VNet flow logs
      // but for backwards compatibility in case azure_collect_flow_logs property is missing or a string
      const toParse = exportDetails?.properties?.azure_collect_flow_logs || false;
      const parsedAzureFlow = JSON.parse(toParse);
      const generalAzureFlowCollection = flow || parsedAzureFlow;
      const isForVnetCollection =
        exportDetails?.properties?.azure_collect_vnet_flow_logs ||
        exportDetails?.properties?.enable_azure_vnet_log_collection ||
        exportDetails?.properties?.azure_enable_azure_vnet_log_collection;
      const isMetadataOnly = exportDetails?.properties?.azure_metadata_only;
      // if we're not metadata-only, collecting vnet logs or if we're already populating log_collection_types with 'flow'
      // and we do have collect_flow_logs, enable_flow_log_collection or azure_collect_flow_logs
      // we want to make sure we include 'flow' in the log_collection_types for NSG flow log collection,
      // as long as the user didn't explicitly disable NSG flow log collection by unchecking the box in the UI
      if (
        generalAzureFlowCollection &&
        !isMetadataOnly &&
        // as long as the deserialization logic that sets azure_collect_nsg_legacy_flow_logs
        // to true for older exports with flow collection enabled is in place,
        // we can use the presence of the azure_collect_nsg_legacy_flow_logs property
        // to determine whether or not to include 'flow' in the log_collection_types for these older exports,
        exportDetails?.properties?.azure_collect_nsg_legacy_flow_logs
      ) {
        logCollectionTypes.push('flow');
      }

      if (isForVnetCollection) {
        logCollectionTypes.push('vnet');
      }
    } else if (flow) {
      logCollectionTypes.push('flow');
    }

    if (exportDetails?.properties?.enable_firewall_log_collection) {
      logCollectionTypes.push('firewall');
    }

    if (gcpCloudRun) {
      logCollectionTypes.push('gcp_cloud_run');
    }

    return logCollectionTypes;
  }

  serialize(data) {
    const { properties } = data;
    if (properties.apply_bgp) {
      if (properties.device_bgp_type !== 'device') {
        properties.device_bgp_neighbor_ip = null;
        properties.device_bgp_neighbor_ip6 = null;
        properties.device_bgp_neighbor_asn = null;
        properties.device_bgp_password = null;
        properties.device_bgp_flowspec = false;
        properties.device_bgp_label_unicast = false;
        properties.bgpPeerIP4 = null;
        properties.bgpPeerIP6 = null;
      }
    }

    if (properties.aws_metadata_only) {
      properties.aws_bucket = 'KENTIK-NO-FLOW';
    }

    if (!properties.azure_collect_flow_logs) {
      properties.azure_storage_account = 'kentik-no-flow';
    }

    if (data.cloud_provider) {
      data.properties.log_collection_types = this.getLogCollectionTypes({
        cloudProvider: data.cloud_provider,
        exportDetails: data
      });

      // if we don't have a cloud provider here, it's likely that we're executing a patch update for task status
      // in this case we're not editing the enrichment scope and will want to skip this part
      if (data.cloud_provider === 'azure') {
        // azure only! a log_collection_types value of 'flow' means an older export
        // that was created before we released the vnet log collection feature
        // previously we'd set the azure_collect_flow_logs property to indicate this
        // but that property is overloaded now that we're also collecting VNet flow logs
        // so set azure_collect_nsg_legacy_flow_logs to disabiguate
        if (data.properties.log_collection_types.includes('flow')) {
          data.properties.azure_collect_nsg_legacy_flow_logs = true;
        }

        if (
          data.properties.azure_enable_azure_vnet_log_collection ||
          data.properties.enable_azure_vnet_log_collection
        ) {
          // if the old vnet log collection properties are enabled,
          // we want to make sure the new one is also enabled for backwards compatibility
          data.properties.azure_collect_vnet_flow_logs = true;
        }
        // clean out temporary properties in the enrichment scope and trim resource groups if needed
        properties.azure_enrichment_scope = properties.azure_enrichment_scope?.reduce((acc, item) => {
          const { isValid = true, message, ...cleanedItem } = item;

          if (isValid) {
            if (cleanedItem.resourceGroups?.length === 0) {
              // when resource groups are empty, just remove it altogether
              delete cleanedItem.resourceGroups;
            }

            return acc.concat(cleanedItem);
          }

          return acc;
        }, []);
      } else {
        // only send the enrichment scope for azure providers
        delete properties.azure_enrichment_scope;
      }
    }

    if (properties.oci_metadata_only && properties.oci_bucket_name === 'KENTIK-NO-FLOW') {
      // as of Sep 2025, transflou logic doesn't look for 'KENTIK-NO-FLOW' for OCI the way it does for AWS
      // so we need to clear that value out of the properties before saving to DB.
      properties.oci_bucket_name = '';
      properties.oci_bucket_namespace_name = '';
      properties.oci_service_connector_ocid = '';
    }

    return super.serialize(data);
  }

  deserialize(data) {
    if (data) {
      const { properties } = data;

      if (properties) {
        // for aws and azure we set the bucket value to 'KENTIK-NO-FLOW' (uppercase for aws, lowercase for azure) when it's a metadata-only export,
        // but we'll clear it out here in the deserialize method so that it doesn't appear in the edit form and cause confusion for users.
        if (properties.aws_bucket === 'KENTIK-NO-FLOW') {
          properties.aws_metadata_only = true;
          properties.aws_bucket = '';
        }
        if (properties.azure_storage_account === 'kentik-no-flow') {
          properties.azure_storage_account = '';
          properties.azure_collect_flow_logs = false;
        } else {
          properties.azure_collect_flow_logs = true;
        }

        if (data.cloud_provider === 'azure') {
          // ensure azure location value is valid, if not set task status to error and populate error message
          if (properties.azure_location) {
            const validLocation = $dictionary.getCloudMetadataKeyToNameMap('azure')[properties.azure_location];
            if (!validLocation) {
              data.properties.task_status = 'ERROR';
              data.properties.error_message = `Cloud Export location value "${properties.azure_location}" is invalid.  Please edit the cloud export and select a valid location from the drop-down list.`;
            }
          }
          // standardize the vnet log collection properties to use the azure_collect_vnet_flow_logs property
          // in case we have some older exports that are still using the azure_enable_azure_vnet_log_collection and enable_azure_vnet_log_collection properties
          if (properties.azure_enable_azure_vnet_log_collection || properties.enable_azure_vnet_log_collection) {
            properties.azure_collect_vnet_flow_logs = true;
          }
          // if we have vnet log collection enabled but neither of the vnet log collection properties are set to true,
          // default to enabling the azure_collect_vnet_flow_logs property for any exports that might have been created
          // before we released the vnet log collection feature and didn't have these properties in place yet
          if (
            properties.log_collection_types?.includes('vnet') &&
            !properties.azure_collect_vnet_flow_logs &&
            !properties.azure_enable_azure_vnet_log_collection &&
            !properties.enable_azure_vnet_log_collection
          ) {
            properties.azure_collect_vnet_flow_logs = true;
          }

          if (properties.azure_collect_vnet_flow_logs) {
            properties.log_collection_types.push('vnet');
          }

          // If we have flow log collection enabled
          // then we'll assume this is an older export that was created before we released the vnet log collection feature
          // when NSG flow log collection was the only option,
          // set the azure_collect_nsg_legacy_flow_logs property to true for these exports
          // so that we can continue collecting flow logs for them until we can remove support for the old properties in a future release.
          if (
            properties.log_collection_types?.includes('flow')
            // &&
            //  !properties.azure_collect_vnet_flow_logs &&
            //  !properties.azure_enable_azure_vnet_log_collection &&
            //  !properties.enable_azure_vnet_log_collection
          ) {
            properties.azure_collect_nsg_legacy_flow_logs = true;
          }
        }
      }
    }

    return super.deserialize(data);
  }
}
